import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Alert, Col, Container, Row } from "reactstrap";
import { fetchPosCompanyAccounts } from "../../../api/requests/pos-analytics-requests";
import PosScopeTable from "./PosScopeTable";
import PosTransactionsTable, { TRANSACTION_FILTER_FIELDS } from "./PosTransactionsTable";
import {
    Loading,
    NAVY,
    Pager,
    PosBreadcrumb,
    PosFilterBar,
    PosPageHeader,
    POS_REPORT_ROOT,
    StatCard,
    fmtMoney2,
    providerLabelOf,
    trNum,
    useIsSuperAdmin,
    usePosDateRange,
    usePosFilters,
    usePosSort,
} from "./PosReportShared";

import { useAutoRefresh, AUTO_REFRESH_MS } from "../../../hooks/useAutoRefresh";
import RefreshIndicator from "../../../components/Common/RefreshIndicator";
/* ============================================================================
 * POS Rapor → Sağlayıcı → Firma detayı.
 *
 * Tasarım notu gereği burada iki sekme vardır:
 *   - CARİ BAZLI : firmanın bu POS üzerinden işlem geçen carileri
 *   - İŞLEM BAZLI: aynı kapsamdaki işlem dökümü
 *
 * Cari satırına tıklandığında cari detayına (ve oradan cari bazlı işlem
 * detayına) inilir.
 * ========================================================================== */

const TABS = [
    { key: "cari", label: "Cari Bazlı" },
    { key: "islem", label: "İşlem Bazlı" },
];

/** Cari tablosunun gelişmiş filtreleri — değerler backend'e gider (sayfalı liste). */
const ACCOUNT_FILTER_FIELDS = [
    { key: "search", type: "text", label: "Cari Adı / Kodu", placeholder: "Cari adı veya kodunda ara" },
    { key: "transactionCount", type: "range", label: "İşlem Sayısı",
      minKey: "minTransactionCount", maxKey: "maxTransactionCount" },
    { key: "grossAmount", type: "range", label: "İşlem Tutarı (₺)",
      minKey: "minGrossAmount", maxKey: "maxGrossAmount" },
    { key: "netAmount", type: "range", label: "Net Tutar (₺)",
      minKey: "minNetAmount", maxKey: "maxNetAmount" },
];

export default function PosCompanyDetailPage({ defaultTab = "cari" }) {
    const { provider, companyId } = useParams();
    const { startDate, endDate, apply, rangeQuery } = usePosDateRange();
    const { isSuperAdmin } = useIsSuperAdmin();
    const [searchParams, setSearchParams] = useSearchParams();

    // Sekme query string'de tutulur; /islemler route'u varsayılanı "islem" yapar.
    const tabParam = searchParams.get("tab");
    const tab = (tabParam || defaultTab) === "islem" ? "islem" : "cari";
    const setTab = (next) => {
        const p = new URLSearchParams(searchParams);
        p.set("tab", next);
        setSearchParams(p, { replace: true });
    };

    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    // Cari listesi sayfalı geldiği için sıralama ve filtreleme backend'de yapılır.
    const { sort, direction, toggle } = usePosSort("grossAmount", "desc");
    const onSort = (key) => { setPage(0); toggle(key); };

    // Filtre alanları sekmeye göre değişir (cari tablosu ↔ işlem dökümü); sekme
    // değişince eldeki değerler ilgisiz kalacağı için sıfırlanır.
    const { draft, applied, set, commit, reset, activeCount } = usePosFilters();
    const onFilterChange = (key, value, immediate) => {
        if (immediate) setPage(0);
        set(key, value, immediate);
    };
    const onFilterCommit = () => { setPage(0); commit(); };
    const isTxnTab = tab === "islem";
    useEffect(() => { reset(); }, [tab, reset]);

    /**
     * @param {{silent?: boolean}} [opts] silent=true otomatik yenilemede kullanılır:
     * tam sayfa spinner'ı açılmaz, hata basılmaz; filtre/sayfa/sıralama korunur.
     */
    const load = useCallback(async ({ silent = false } = {}) => {
        if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const res = await fetchPosCompanyAccounts(provider, companyId, {
                startDate, endDate, page, size: 20, sort, direction,
                // İşlem sekmesindeki filtreler bu uca ait değil; sadece cari sekmesinde gönderilir.
                ...(isTxnTab ? {} : applied),
            });
            if (res?.statusCode === 200 && res.data) setData(res.data);
            else if (!silent && res?.statusCode === 403) setError("Bu sayfayı görüntüleme yetkiniz yok.");
            else if (!silent) setError(res?.message || "Cari listesi alınamadı.");
        } catch (e) {
            if (!silent) setError("Veri yüklenirken bir hata oluştu.");
        } finally {
            if (!silent) setLoading(false);
        }
    }, [provider, companyId, startDate, endDate, page, sort, direction, applied, isTxnTab]);

    // load o anki filtre/sayfa/sıralamayı kapsadığı için sessiz çağrı seçimleri bozmaz.
    const autoRefresh = useAutoRefresh(() => load({ silent: true }), { intervalMs: AUTO_REFRESH_MS });

    useEffect(() => {
        load();
    }, [load]);

    const label = data?.providerLabel || providerLabelOf(provider);
    const companyName = data?.companyName || "Firma";
    const s = data?.summary;
    const base = `${POS_REPORT_ROOT}/${provider}/${companyId}`;

    return (
        <div className="page-content">
            <Container fluid>
                <PosBreadcrumb
                    backTo={`${POS_REPORT_ROOT}/${provider}${rangeQuery}`}
                    items={[
                        { label: "POS Rapor", to: `${POS_REPORT_ROOT}` },
                        { label, to: `${POS_REPORT_ROOT}/${provider}${rangeQuery}` },
                        { label: companyName },
                    ]}
                />
                <PosPageHeader
                    title={companyName}
                    subtitle={`${label}${data?.periodLabel ? " · " + data.periodLabel : ""}`}
                    right={<RefreshIndicator {...autoRefresh} onClick={autoRefresh.refreshNow} />}
                />

                <PosFilterBar
                    startDate={startDate}
                    endDate={endDate}
                    onRangeChange={(s2, e2) => { setPage(0); apply(s2, e2); }}
                    fields={isTxnTab ? TRANSACTION_FILTER_FIELDS(isSuperAdmin) : ACCOUNT_FILTER_FIELDS}
                    values={draft}
                    onChange={onFilterChange}
                    onCommit={onFilterCommit}
                    onReset={() => { setPage(0); reset(); }}
                    activeCount={activeCount}
                />

                {/* Sekmeler */}
                <div className="d-flex gap-2 mb-3">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className="btn btn-sm"
                            style={{
                                borderRadius: 999,
                                padding: ".4rem 1rem",
                                border: `1px solid ${tab === t.key ? NAVY : "#dfe3ea"}`,
                                background: tab === t.key ? NAVY : "#fff",
                                color: tab === t.key ? "#fff" : "#5c6a86",
                                fontWeight: 600,
                            }}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {error && <Alert color="danger">{error}</Alert>}

                {isTxnTab ? (
                    <PosTransactionsTable
                        provider={provider}
                        companyId={companyId}
                        startDate={startDate}
                        endDate={endDate}
                        isSuperAdmin={isSuperAdmin}
                        filters={applied}
                    />
                ) : loading ? (
                    <Loading />
                ) : (
                    <>
                        <Row className="g-3 mb-3">
                            <Col xs={6} md={3}>
                                <StatCard accent label="Çekilen Toplam" value={fmtMoney2(data?.totals?.grossAmount)} />
                            </Col>
                            <Col xs={6} md={3}>
                                <StatCard
                                    label="İşlem Sayısı"
                                    value={trNum(data?.totals?.transactionCount)}
                                    sub={`${trNum(s?.failedCount)} başarısız · ${trNum(s?.pendingCount)} bekleyen`}
                                />
                            </Col>
                            <Col xs={6} md={3}>
                                <StatCard
                                    // Kesilen toplam komisyon: firmanın gideri. Süper admin için
                                    // "hakediş" değildir — platformun payı Komisyon Kazancı kolonudur.
                                    label={isSuperAdmin ? "Toplam Komisyon" : "Komisyon Kesintisi"}
                                    value={fmtMoney2(data?.totals?.commissionAmount)}
                                />
                            </Col>
                            <Col xs={6} md={3}>
                                <StatCard label="Net Tutar" value={fmtMoney2(data?.totals?.netAmount)} />
                            </Col>
                        </Row>

                        <PosScopeTable
                            rows={data?.content || []}
                            totals={data?.totals}
                            showAdminColumns={isSuperAdmin}
                            emptyText="Bu dönemde bu POS üzerinden işlem geçen cari bulunmuyor."
                            identityColumns={[
                                {
                                    key: "accountName",
                                    label: "Cari Adı",
                                    className: "fw-semibold text-dark",
                                    sortKey: "accountName",
                                    render: (r) => r.accountName || "Carisiz",
                                },
                                {
                                    // Tek firmanın carileri listelendiği için bu kolon
                                    // her satırda aynı; sıralanabilir yapılmadı.
                                    key: "companyName",
                                    label: "Firma Adı",
                                    className: "text-muted",
                                    render: () => companyName,
                                },
                            ]}
                            sort={sort}
                            direction={direction}
                            onSort={onSort}
                            footer={
                                <Pager
                                    page={data?.page ?? 0}
                                    totalPages={data?.totalPages}
                                    totalElements={data?.totalElements}
                                    onChange={setPage}
                                />
                            }
                        />
                    </>
                )}
            </Container>
        </div>
    );
}
